// ── Drukte (bezoekers per half uur) ─────────────────────
// Sinds de Sportivity-bezoek-webhook (SendVisits) live staat, loggen we ieder
// bezoek zelf en écht real-time in sportivity_visit_log. Voor "vandaag t/m nu"
// gebruiken we dus onze eigen tellingen (via de database-functie
// halfuur_drukte_vandaag, die alleen geaggregeerde tellingen per half uur
// teruggeeft — nooit individuele bezoeken van leden).
//
// Voor de nog komende half-uur-blokken van vandaag (de "verwachting") gebruiken
// we sinds eind september het écht historische gemiddelde voor die weekdag en
// dat blok (halfuur_verwachting — sportivity_visit_log heeft inmiddels ruim een
// maand aan data, 4-5 gemeten weken per weekdag). Een blok zonder historie
// betekent dat er op die weekdag nooit iemand kwam (0); alleen als er helemaal
// geen historie op te halen is, valt alles terug op TYPISCH_PATROON hieronder.
//
// Zolang de koppeling niet bereikbaar is valt alles terug op voorbeelddata,
// zodat het blokje nooit kapot gaat.
import { supabase } from './supabase'
import { vandaagHalfUurAs } from './openingstijden'

// Typisch patroon per uur (laatste terugvaloptie per blok, gebaseerd op een
// gemiddelde sportschooldag — alleen nog gebruikt zolang halfuur_verwachting()
// voor dat specifieke blok nog geen eigen meting heeft)
const TYPISCH_PATROON = {
  6: 2, 7: 6, 8: 10, 9: 14, 10: 9, 11: 6, 12: 5, 13: 7, 14: 5, 15: 6,
  16: 9, 17: 16, 18: 20, 19: 17, 20: 10, 21: 4,
}

// Gemiddelde duur van één bezoek: een volledige cirkeltraining duurt 35
// minuten, plus een paar minuten stretchen op de Five-toestellen en tijd voor
// omkleden. Gebruikt om de actuele BEZETTING te schatten uit de instroom (er
// wordt niet uitgecheckt, dus dit is een aanname, geen meting).
export const GEMIDDELDE_SESSIEDUUR_MIN = 45

function blokNaarMinuten(blok) {
  const [h, m] = blok.split(':').map(Number)
  return h * 60 + m
}

// Verwachting voor een half-uur-blok: het typische uur-cijfer gelijk verdeeld
// over de twee halve uren van dat uur.
function typischHalfUur(blok) {
  const uur = Number(blok.split(':')[0])
  return (TYPISCH_PATROON[uur] ?? 0) / 2
}

function minutenNaarBlok(min) {
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`
}

function huidigBlok() {
  const nu = new Date()
  const min = Math.floor(nu.getMinutes() / 30) * 30
  return `${String(nu.getHours()).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

function bouwData(bezoekenPerBlok, verwachtingPerBlok = {}) {
  const nuBlok = huidigBlok()
  const nuMin = blokNaarMinuten(nuBlok)
  const blokkenAs = vandaagHalfUurAs()
  const openBlokken = new Set(blokkenAs.filter(b => b.open).map(b => b.blok))

  // Is er echte historie opgehaald, dan betekent een ontbrekend blok "daar
  // kwam nooit iemand" (0) — niet terugvallen op TYPISCH_PATROON. Alleen als er
  // helemaal geen historie is, gebruiken we het generieke patroon.
  const heeftHistorie = Object.keys(verwachtingPerBlok).length > 0
  const verwachtVoor = (blok) => heeftHistorie ? (verwachtingPerBlok[blok] ?? 0) : typischHalfUur(blok)

  return blokkenAs.map(({ blok, open }) => {
    const isVerledenOfNu = blokNaarMinuten(blok) <= nuMin
    // Leden checken vaak al in het kwartier vóór openingstijd in (bv. 7:45 op
    // een dinsdag). Die trainen in het eerste open half uur, dus tellen we het
    // half uur vóór een opening daarbij op.
    const vorigBlok = minutenNaarBlok(blokNaarMinuten(blok) - 30)
    const vroegeCheckins = open && heeftHistorie && !openBlokken.has(vorigBlok)
      ? (verwachtingPerBlok[vorigBlok] ?? 0)
      : 0
    const verwachteWaarde = verwachtVoor(blok) + vroegeCheckins
    return {
      blok,
      gesloten: !open,
      bezoeken: !open ? 0 : (isVerledenOfNu ? (bezoekenPerBlok[blok] ?? 0) : verwachteWaarde),
      // Blokken ná dit moment zijn een inschatting (verwachting), geen meting.
      verwacht: open && !isVerledenOfNu,
      // De historische verwachting voor dit blok, ook voor blokken die al
      // voorbij zijn — gebruikt voor de staafjes "verwachting per uur".
      verwachting: !open ? 0 : verwachteWaarde,
    }
  })
}

// Geeft { data, live, bezettingNu } terug — live is true zodra de echte
// (real-time) bezoektellingen zijn gebruikt, zodat de UI de
// "voorbeelddata"-melding alleen toont als de koppeling (nog) niet lukt.
// bezettingNu is de geschatte actuele bezetting (zie bepaalDrukteNiveau),
// of null als die niet opgehaald kon worden.
export async function getDrukteData() {
  try {
    const { data, error } = await supabase.rpc('halfuur_drukte_vandaag')
    if (error) throw error
    const perBlok = {}
    ;(data ?? []).forEach(r => { perBlok[r.blok] = Number(r.bezoeken) })

    // Verwachting voor de nog komende blokken van vandaag: het echte
    // historische gemiddelde voor deze weekdag (zelfde 0=zondag-conventie als
    // de SQL-functie). Lukt dit niet, dan valt bouwData() vanzelf per blok
    // terug op TYPISCH_PATROON.
    let verwachtingPerBlok = {}
    try {
      const { data: verwachting, error: vErr } = await supabase.rpc('halfuur_verwachting', { doelweekdag: new Date().getDay() })
      if (vErr) throw vErr
      ;(verwachting ?? []).forEach(r => { verwachtingPerBlok[r.blok] = Number(r.gemiddelde) })
    } catch {
      // val terug op TYPISCH_PATROON per blok, zie bouwData()
    }

    // Actuele bezetting: rollend venster van de laatste GEMIDDELDE_SESSIEDUUR_MIN
    // minuten, i.p.v. alleen het lopende half-uur-blok — zie bepaalDrukteNiveau.
    let bezettingNu = null
    try {
      const { data: bezetting, error: bErr } = await supabase.rpc('bezetting_nu')
      if (bErr) throw bErr
      bezettingNu = bezetting == null ? null : Number(bezetting)
    } catch {
      // bezettingNu blijft null; bepaalDrukteNiveau valt dan terug op data[]
    }

    return { data: bouwData(perBlok, verwachtingPerBlok), live: true, bezettingNu }
  } catch {
    return { data: bouwData({}), live: false, bezettingNu: null }
  }
}

// Geeft alleen de nog komende (verwachte) blokken van vandaag terug — voor de
// "verwachting voor de komende uren"-strook onder de drukte-meter.
export function komendeVerwachting(data) {
  if (!data) return []
  return data.filter(d => d.verwacht)
}

// Referentiepiek: het aantal gelijktijdige bezoekers dat als "vol"/Druk geldt.
// Dit is bewust gekoppeld aan de fysieke capaciteit van de Milon Cirkel — daar
// staan maar 12 toestellen, dus veel meer dan 12 mensen tegelijk "in training"
// is er praktisch niet. Toeval of niet: dit kwam al overeen met het cijfer dat
// Marcel eerder handmatig vaststelde op basis van de ochtenddrukte van
// 26-08-2026 (drukste half uur toen: 7 bezoeken, met marge naar 12).
//
// Let op: een rollend venster van 45 minuten (zie bepaalDrukteNiveau) kan door
// doorstroom op de cirkel best vaker boven de 12 uitkomen dan er ooit écht
// mensen tegelijk stonden — iemand die na 20 minuten alweer klaar is en weg is,
// telt in dat venster nog even mee. Dat is geen reden om de referentiepiek te
// verhogen: bij 12 of meer is de cirkel realistisch gezien vol of loopt er een
// wachtrij, en dát is precies "Druk".
export const REFERENTIE_PIEK = 12

// Bepaalt het huidige drukteniveau (badge). Gebruikt bij voorkeur de actuele
// BEZETTING — een rollend venster van de laatste GEMIDDELDE_SESSIEDUUR_MIN
// minuten (bezettingNu, uit de database-functie bezetting_nu()) — in plaats
// van alleen het lopende half-uur-blok. Er wordt niet uitgecheckt, dus een lid
// dat net is ingecheckt telt de hele sessieduur mee, ook als er daarna een tijd
// niemand bijkomt: dat voorkomt dat de meter "rustig" toont vlak nadat het
// juist erg druk was.
//
// Is bezettingNu niet beschikbaar (bv. omdat de koppeling niet lukt en we op
// voorbeelddata draaien), dan valt de functie terug op het dichtstbijzijnde
// half-uur-cijfer uit `data`, opgeschaald naar een 45-minuten-equivalent
// (×1,5 — 45 minuten t.o.v. 30) zodat de badge ongeveer op hetzelfde niveau
// blijft.
//
// Vier niveaus, met vaste grenzen op de geschatte bezetting t.o.v. de 12
// toestellen — ongewijzigd t.o.v. de oorspronkelijke grenzen, nu toegepast op
// de bezetting i.p.v. alleen de instroom van het lopende half uur:
//   0-3   Rustig
//   4-6   Redelijk rustig
//   7-10  Gemiddeld
//   11+   Druk
export function bepaalDrukteNiveau(data, bezettingNu = null) {
  let waarde = bezettingNu
  if (waarde == null) {
    if (!data || data.length === 0) {
      waarde = 0
    } else {
      const nuBlok = huidigBlok()
      const nuMin = blokNaarMinuten(nuBlok)
      const basis = data.find(d => d.blok === nuBlok && !d.gesloten)
        || data.reduce((dichtst, d) =>
          Math.abs(blokNaarMinuten(d.blok) - nuMin) < Math.abs(blokNaarMinuten(dichtst.blok) - nuMin) ? d : dichtst,
          data[0])
      waarde = basis.bezoeken * 1.5
    }
  }

  const ratio = Math.max(0, Math.min(1, waarde / REFERENTIE_PIEK))
  if (waarde >= 11) return { label: 'Druk', tekst: '#c0392b', achtergrond: '#fdecea', ratio }
  if (waarde >= 7) return { label: 'Gemiddeld', tekst: 'var(--orange)', achtergrond: 'var(--orange-light)', ratio }
  if (waarde >= 4) return { label: 'Redelijk rustig', tekst: 'var(--green-dark)', achtergrond: 'var(--green-light)', ratio }
  return { label: 'Rustig', tekst: 'var(--green-dark)', achtergrond: 'var(--green-light)', ratio }
}
